import React, { useState } from 'react';
import api from '../services/api';

export default function Reports() {
  const [reportType, setReportType] = useState('tenant_summary');
  const [params, setParams] = useState({ startDate: '', endDate: '', tenant_code: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    { id: 'tenant_summary', label: 'Tenant Summary', desc: 'Overview of all tenants with subscription and usage data', icon: '🏢' },
    { id: 'subscription', label: 'Subscription Report', desc: 'Revenue, plan distribution, expirations, and renewals', icon: '💳' },
    { id: 'usage', label: 'Usage Report', desc: 'Tenant database sizes, user counts, and activity metrics', icon: '📊' },
    { id: 'audit', label: 'Audit Report', desc: 'Platform-wide activity summary and action breakdown', icon: '🔍' },
  ];

  const generate = async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (params.startDate) qp.append('startDate', params.startDate);
      if (params.endDate) qp.append('endDate', params.endDate);
      if (params.tenant_code) qp.append('tenant_code', params.tenant_code);
      const res = await api.get(`/reports/${reportType}?${qp}`);
      setReport(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!report?.rows?.length) return;
    const headers = Object.keys(report.rows[0]);
    const csv = [headers.join(','), ...report.rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${reportType}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Reports</h2>
        <p className="text-sm text-gray-500 mt-0.5">Generate platform reports for analysis and record-keeping</p>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {reportTypes.map(rt => (
          <button key={rt.id} onClick={() => { setReportType(rt.id); setReport(null); }}
            className={`text-left p-4 rounded-xl border-2 transition-all ${reportType === rt.id ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
            <div className="text-2xl mb-2">{rt.icon}</div>
            <div className="text-sm font-semibold text-gray-900">{rt.label}</div>
            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{rt.desc}</div>
          </button>
        ))}
      </div>

      {/* Filters & Generate */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <input type="date" value={params.startDate} onChange={e => setParams(p => ({ ...p, startDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <input type="date" value={params.endDate} onChange={e => setParams(p => ({ ...p, endDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tenant Code (optional)</label>
            <input value={params.tenant_code} onChange={e => setParams(p => ({ ...p, tenant_code: e.target.value }))} placeholder="All tenants" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <button onClick={generate} disabled={loading} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Output */}
      {report && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{report.title || 'Report'}</h3>
              <p className="text-xs text-gray-400 mt-0.5">Generated: {report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString()}</p>
            </div>
            {report.rows?.length > 0 && (
              <button onClick={exportCSV} className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                <span>Export CSV</span>
              </button>
            )}
          </div>

          {/* Summary */}
          {report.summary && (
            <div className="p-4 border-b border-gray-100 bg-gray-50/50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(report.summary).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-gray-500 uppercase">{k.replace(/_/g, ' ')}</div>
                    <div className="text-lg font-bold text-gray-900 mt-0.5">{typeof v === 'number' && k.includes('revenue') ? `₹${v.toLocaleString()}` : v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Table */}
          {report.rows?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                  {Object.keys(report.rows[0]).map(h => <th key={h} className="px-4 py-2.5 text-left">{h.replace(/_/g, ' ')}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {report.rows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 text-sm">
                      {Object.values(row).map((v, j) => <td key={j} className="px-4 py-2.5 text-gray-700">{v != null ? String(v) : '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-sm text-gray-400">No data found for selected criteria</div>
          )}
        </div>
      )}
    </div>
  );
}
